import React, { useEffect, useState } from "react";
import axios from "../../utils/axios";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

const AdminOrderManagement = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [expandedOrder, setExpandedOrder] = useState(null);
    const [statusChanges, setStatusChanges] = useState({});

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                const response = await axios.get(`/api/admin/orders?page=${page}&limit=10`);
                setOrders(response.data.orders);
                setTotalPages(response.data.totalPages);
            } catch (error) {
                console.error("Error fetching orders:", error);
                setError("Failed to load orders.");
            } finally {
                setLoading(false);
            }
        };

        fetchOrders();
    }, [page]);

    const handleStatusChange = async (orderId, newStatus) => {
        setStatusChanges((prev) => ({
                ...prev,
                [orderId]: newStatus,
            }));
    };

    const handleSaveStatusChange = async (orderId) => {
        const newStatus = statusChanges[orderId];
        try {
            await axios.put(`/api/admin/orders/${orderId}`, { status: newStatus });
            setOrders(orders.map(order =>
                order._id === orderId ? { ...order, status: newStatus } : order
            ));
            setStatusChanges((prev) => {
                    const { [orderId]: _, ...rest } = prev;
                    return rest;
            });
            toast.info("Updated successfully");
        } catch (error) {
            if (error.response && error.response.data && error.response.data.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error("Failed to update order status.");
            }
        }
    }

    const toggleOrderDetails = (orderId) => {
        setExpandedOrder(expandedOrder === orderId ? null : orderId);
    };

    const truncateTitle = (title) => {
        return title.length > 80 ? `${title.slice(0, 77)}...` : title;
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case -1: return "Cancelled";
            case 0: return "Ordered";
            case 1: return "Shipped";
            case 2: return "Delivered";
            default: return "Unknown";
        }
    };

    if (loading) {
        return <div className="text-center">Loading...</div>;
    }

    if (error) {
        return <div className="text-center text-red-500">{error}</div>;
    }

    return (
        <div className="container mx-auto p-6 bg-white shadow-md rounded-lg">
            <h1 className="text-3xl font-semibold text-center mb-6">Admin Order Management</h1>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-300">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="px-4 py-2 text-left">Order ID</th>
                            <th className="px-4 py-2 text-left">User</th>
                            <th className="px-4 py-2 text-left">Created At</th>
                            <th className="px-4 py-2 text-left">Grand Total</th>
                            <th className="px-4 py-2 text-left">Payment Method</th>
                            <th className="px-4 py-2 text-left">Status</th>
                            <th className="px-4 py-2 text-left">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orders.map((order) => (
                            <React.Fragment key={order._id}>
                                <tr
                                    className="hover:bg-gray-100 cursor-pointer"
                                    onClick={() => toggleOrderDetails(order._id)}
                                >
                                    <td className="border px-4 py-2">{order._id}</td>
                                    <td className="border px-4 py-2">{order.user ? order.user.name : ''}</td>
                                    <td className="border px-4 py-2">{(new Date(Number(order.createdAt))).toLocaleString()}</td>
                                    <td className="border px-4 py-2">${order.grandTotal.toFixed(2)}</td>
                                    <td className="border px-4 py-2">{order.paymentMethod}</td>
                                    <td className="border px-4 py-2">
                                        {(order.status === 0 || order.status === 1) ? (
                                            <select
                                                value={statusChanges[order._id] !== undefined ? statusChanges[order._id] : order.status}
                                                onChange={(e) => handleStatusChange(order._id, Number(e.target.value))}
                                                className="border rounded p-1"
                                            >
                                                <option value={0}>Ordered</option>
                                                <option value={1}>Shipped</option>
                                                <option value={2}>Delivered</option>
                                                <option value={-1}>Cancelled</option>
                                            </select>
                                        ) : (<span
                                                className={`${order.status === 2 ? 'text-green-500' : 'text-red-500'}`}>
                                                {getStatusLabel(order.status)}
                                            </span>
                                        )}
                                    </td>
                                    <td className="border px-4 py-2">
                                        <button
                                            onClick={() => handleSaveStatusChange(order._id)}
                                            disabled={statusChanges[order._id] === undefined || statusChanges[order._id] === order.status}
                                            className={`py-1 px-3 rounded-lg transition-all duration-300
                                                ${statusChanges[order._id] === undefined || statusChanges[order._id] === order.status
                                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                        : 'bg-blue-500 text-white hover:bg-blue-700'}`}
                                        >
                                            Save
                                        </button>
                                    </td>
                                </tr>
                                {expandedOrder === order._id && (
                                    <tr>
                                        <td colSpan="6" className="border-t">
                                            <div className="p-4">
                                                <ul className="list-disc pl-5">
                                                    {order.items.map((item) => (
                                                        <li key={item._id} className="flex items-center justify-between">
                                                            <Link to={`/product/${item.pid}`} className="flex items-center text-blue-500 hover:underline">
                                                                <img src={item.cover} alt={item.title} className="w-16 h-16 object-cover mr-4" />
                                                                <span>{truncateTitle(item.title)}</span>
                                                            </Link>
                                                            <span>${item.price.toFixed(2)} x {item.quantity}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="flex justify-between mt-4">
                <button
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                    className={`py-2 px-4 rounded-lg transition-all duration-300
                        ${page === 1 ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-blue-500 text-white hover:bg-blue-700'}`}
                >
                    Previous
                </button>
                <span className="self-center">Page {page} of {totalPages}</span>
                <button
                    disabled={page === totalPages}
                    onClick={() => setPage(page + 1)}
                    className={`py-2 px-4 rounded-lg transition-all duration-300
                        ${page === totalPages ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-blue-500 text-white hover:bg-blue-700'}`}
                >
                    Next
                </button>
            </div>
        </div>
    );
};

export default AdminOrderManagement;

